// src/pages/cart/hooks/useCartModal.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  CartModalStage,
  DeliveryService,
  DeliveryType,
  GoalType,
  PaymentMethod,
} from "../../../../common/types/enums";

import {
  getFromStorage,
  removeFromStorage,
  saveToStorage,
} from "../../../../common/utils/functions/localStorage";
import { setOrders } from "../../../../store/slices/orderSlice";
import {
  setCart,
  addGift,
  removeGift,
} from "../../../../store/slices/cartSlice";

import { useCartData } from "./useCartData";
import { useAddressManagement } from "./useAddressManagement";
import { useCartCalculations } from "./useCartCalculations";
import { usePromoManagement } from "./usePromoManagement";
import { useBonuses } from "./useBonuses";
import { useOrderCreation } from "./useOrderCreation";
import { useDeliveryCalculator } from "./useDeliveryCalculator";
import { useDeliveryTime } from "./useDeliveryTime";
import { getApi } from "../../../../api/api";
import { useCartValidation } from "./useCartValidation";
import { useCartStoreCheck } from "./useCartStoreCheck";
import { CartItem, OrderItem } from "../../../../common/types/types";
import { closeCartModal } from "../../../../store/slices/commonModalSlice";
import { selectPaymentMethods } from "../../../../store/slices/paymentMethodsSlice";
import { useOrderDetails } from "./useOrderDetails";
import { useAuthRoute } from "../../../hooks/useAuthRoute";
import { selectForceStores } from "../../../../store/slices/storeListSlice";
import useMetrics from "../../../hooks/useMetrics";
import {
  selectIsAdmin,
  selectIsExecutor,
} from "../../../../store/slices/executorSlice";
import { useCdekDelivery } from "./useCdekDelivery";
import { getErrorMessage } from "../../../utils/functions/getErrorMessage";

export const useCartModal = () => {
  const { trackGoal } = useMetrics();
  const selectedStoreIds = useSelector(selectForceStores);
  const getAuthRoute = useAuthRoute();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const api = getApi();
  const savedCards = useSelector(selectPaymentMethods);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(() => {
    const defaultCard = savedCards.find((c) => c.is_default);
    return defaultCard?.id || null;
  });

  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const isManager = isExecutor || isAdmin;

  const [paymentToken, setPaymentToken] = useState<string>();
  const [paymentId, setPaymentId] = useState<string>();
  const [paymentUrl, setPaymentUrl] = useState<string>();

  const [isOrderCreating, setIsOrderCreating] = useState(false);

  const [selectedGiftItemId, setSelectedGiftItemId] = useState<
    number | undefined
  >();

  const handleCardSelect = useCallback((id: number | null) => {
    setSelectedCardId(id);
    if (id) {
      setPaymentMethod(PaymentMethod.ONLINE);
    }
  }, []);

  const [bonusMode, setBonusMode] = useState<"spend" | "earn">("earn");

  const { currentCluster, cart, saleItemList, storeList, currentUser } =
    useCartData();
  const forceStore = currentCluster?.forceStore ?? false;
  const address = useAddressManagement();
  const bonuses = useBonuses();
  const stockCheck = useCartStoreCheck(cart.items, saleItemList);

  const {
    phone,
    comment,
    getValidationResult,
    inputHandler,
    setDirty,
    isValid: orderDetailsValid,
    MAX_COMMENT_LENGTH,
    cutleryCount,
    setCutleryCount,
    incrementCutlery,
    decrementCutlery,
  } = useOrderDetails();

  const [stage, setStage] = useState<CartModalStage>(CartModalStage.CART);
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(
    DeliveryType.UNSET,
  );
  const [pickUpStore, setPickUpStore] = useState<number | undefined>(
    storeList.length === 1 ? storeList[0].id : undefined,
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.UNSET,
  );
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [orderDisabled, setOrderDisabled] = useState<boolean>(true);
  const [storeCheckResult, setStoreCheckResult] = useState<any | null>(null);

  const [validationError, setValidationError] = useState<string | null>(null);

  const itemsForOrder = storeCheckResult?.itemsForOrder || cart.items;
  const itemsRemaining = storeCheckResult?.itemsRemaining || [];

  const isYookassaEnabled = currentCluster?.yookassa?.enabled ?? false;

  const {
    available: deliveryAvailable,
    deliveryStoreId,
    deliveryDistance,
    deliveryFee: calculatedDeliveryFee,
    loading: deliveryLoading,
    error: deliveryError,
    applicableRules,
  } = useDeliveryCalculator({
    cartItems: itemsForOrder,
    deliveryAddress: address.selectedAddress
      ? {
          latitude: Number(address.selectedAddress.address.latitude),
          longitude: Number(address.selectedAddress.address.longitude),
        }
      : null,
    deliveryType,
  });

  const {
    options: cdekOptions,
    loading: cdekLoading,
    error: cdekError,
    selectedTariff,
    selectedPickupPoint,
    setSelectedTariff,
    setSelectedPickupPoint,
    deliveryService,
    cdekTariffCode,
    setDeliveryService,
    setCdekTariffCode,
    applyMarkup,
  } = useCdekDelivery({
    deliveryType,
    storeId: deliveryStoreId || pickUpStore || null,
    cityId: address.selectedAddress?.city?.id || null,
    addressId: address.selectedAddressId || null,
  });

  useEffect(() => {
    if (deliveryStoreId) {
      const result = stockCheck.checkStore(deliveryStoreId);
      setStoreCheckResult(result);
    }
  }, [deliveryStoreId]);

  const {
    desiredTime,
    displayText: deliveryTimeDisplay,
    pickerVisible: deliveryTimePickerVisible,
    openPicker: openDeliveryTimePicker,
    closePicker: closeDeliveryTimePicker,
    handleTimeSelect: onDeliveryTimeSelect,
    minimumDate: deliveryTimeMinimumDate,
    isTimeAvailable,
  } = useDeliveryTime({
    deliveryType,
    selectedStore: storeList.find((s) => s.id === pickUpStore),
  });

  const promo = usePromoManagement({
    cartItems: itemsForOrder,
    deliveryType,
    pickUpStore,
    onGiftAutoSelect: setSelectedGiftItemId,
  });
  const {
    getBestDeliveryRuleText,
    roundMoney,
    calculateItemsTotal,
    isPromoApplicable,
    findApplicablePromos,
    calculateMaxWriteOff,
    calculateEarnedBonuses,
    getNextBonusLevelText,
  } = useCartValidation();

  const deliveryFee =
    deliveryService === DeliveryService.CDEK && selectedTariff
      ? applyMarkup(selectedTariff.delivery_sum)
      : calculatedDeliveryFee;

  const calculations = useCartCalculations({
    cartItems: itemsForOrder,
    deliveryType,
    pickUpStore,
    selectedPromo: promo.selectedPromo,
    discountCode: promo.discountCode,
    bonusesToSpend: bonuses.bonusesToSpend,
    maxWriteOff: calculateMaxWriteOff(
      itemsForOrder.reduce(
        (sum: number, item: OrderItem) => sum + item.price * item.quantity,
        0,
      ),
      bonuses.balance,
      bonuses.writeOffPercent,
    ),
    deliveryFee: deliveryFee,
    deliveryDistance,
    applicableRules,
  });

  const getOrderList = useCallback(async () => {
    try {
      const result = await api.getOrderList();
      if (result) dispatch(setOrders(result));
    } catch (error) {
      console.error("Error getting order list:", error);
    }
  }, [api, dispatch]);

  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentError, setPaymentError] = useState<string>();

  const { createOrder: createOrderBase } = useOrderCreation({
    cartItems: itemsForOrder,
    saleItemList,
    itemsTotal: calculations.itemsTotal,
    deliveryFee: calculations.deliveryFee,
    discountAmount: calculations.discountAmount,
    totalWithDelivery: calculations.totalWithDelivery,
    deliveryType,
    pickUpStore,
    deliveryStoreId,
    selectedAddressId: address.selectedAddressId,
    paymentMethod,
    selectedPromo: promo.selectedPromo,
    discountCode: promo.discountCode,
    bonusesToSpend: calculations.actualBonusesToSpend,
    desiredTime: desiredTime ?? deliveryTimeMinimumDate?.toISOString() ?? null,
    getOrderList,
    paymentMethodId: selectedCardId || undefined,
    phone,
    comment,
    cutleryCount,
    selectedGiftItemId,
    deliveryService,
    cdekOrder:
      deliveryService === DeliveryService.CDEK && selectedTariff
        ? {
            tariffCode: selectedTariff.tariff_code,
            pickupPointCode: selectedPickupPoint?.code,
            pickupPointName: selectedPickupPoint?.name,
            pickupPointAddress:
              selectedPickupPoint?.location?.address_full ||
              selectedPickupPoint?.address,
            pickupPointPhone: selectedPickupPoint?.phones?.[0]?.number,
            tariffName: selectedTariff.tariff_name,
            periodMin: selectedTariff.period_min,
            periodMax: selectedTariff.period_max,
            deliveryDateMin: selectedTariff.delivery_date_range?.min,
            deliveryDateMax: selectedTariff.delivery_date_range?.max,
          }
        : undefined,
  });
  const handleCreateOrder = useCallback(async () => {
    if (isOrderCreating) return;
    setIsOrderCreating(true);
    try {
      setValidationError(null);
      setPaymentFailed(false);
      setPaymentError(undefined);
      const result = await createOrderBase();

      if (result?.id) {
        setOrderNumber(result.id);
        trackGoal(GoalType.ORDER_COMPLETE, String(result.id), {
          amount: calculations.totalWithDelivery,
          items: itemsForOrder.length,
        });

        if (result?.paymentFailed) {
          setPaymentFailed(true);
          setPaymentError(result.paymentError);
          setStage(CartModalStage.ORDER_SUCCESS);
        } else if (result.paymentToken) {
          setPaymentToken(result.paymentToken);
          setPaymentId(result.paymentId);
          setStage(CartModalStage.YOOKASSA_PAYMENT);
        } else if (result.paymentUrl) {
          setPaymentUrl(result.paymentUrl);
          setStage(CartModalStage.PAYMASTER_PAYMENT);
        } else {
          setStage(CartModalStage.ORDER_SUCCESS);
        }

        if (itemsRemaining.length > 0) {
          dispatch(setCart(itemsRemaining));
        }
      }
    } catch (error: any) {
      console.error("Order error:", error);
      setValidationError(getErrorMessage(error));
    } finally {
      setIsOrderCreating(false);
    }
  }, [isOrderCreating, createOrderBase, itemsRemaining, dispatch]);

  const checkAfterAction = useCallback(async () => {
    const afterAction = getFromStorage("afterAction");
    if (afterAction === "cart" && currentUser) {
      removeFromStorage("afterAction");
      setStage(CartModalStage.CHECKOUT);
    }
  }, [currentUser]);

  useEffect(() => {
    if (stage === CartModalStage.CHECKOUT) {
      address.loadAddresses();
      bonuses.loadBonuses();
    }
  }, [stage]);

  const getSaleItemName = useCallback(
    (id: number): string => {
      const current = saleItemList.find((el) => el.id === id);
      if (!current) return `Товар #${id}`;
      return current.name || `Товар #${id}`;
    },
    [saleItemList],
  );

  const goBack = useCallback(() => {
    switch (stage) {
      case CartModalStage.CHECKOUT:
        setStage(CartModalStage.CART);
        break;
      case CartModalStage.ADD_ADDRESS:
        setStage(CartModalStage.CHECKOUT);
        break;
      case CartModalStage.ORDER_SUCCESS:
        setStage(CartModalStage.CART);
        setOrderNumber(null);
        promo.setSelectedPromo(null);
        promo.setDiscountCode("");
        bonuses.setBonusesToSpend(0);
        setStoreCheckResult(null);
        setSelectedGiftItemId(undefined);
        break;
    }
  }, [stage, promo, bonuses]);

  const handleDeliveryTypeChange = useCallback(
    (type: DeliveryType) => {
      setDeliveryType(type);
      if (type !== DeliveryType.PICKUP) {
        promo.setSelectedPromo(null);
        promo.setDiscountCode("");
        setStoreCheckResult(null);
        setSelectedGiftItemId(undefined);
      }
      if (type === DeliveryType.PICKUP) {
        address.setSelectedAddressId(null);
      }
    },
    [promo, address],
  );

  const handlePickUpStoreChange = useCallback(
    (id: number) => {
      setPickUpStore(id);

      const result = stockCheck.checkStore(id);
      setStoreCheckResult(result);

      if (promo.selectedPromo && promo.selectedPromo.useStores) {
        if (!promo.selectedPromo.storesFor?.includes(id)) {
          promo.setSelectedPromo(null);
          promo.setDiscountCode("");
          setSelectedGiftItemId(undefined);
        }
      }
    },
    [promo, stockCheck],
  );

  const handleAddressSelect = useCallback(
    (addressId: number) => {
      address.setSelectedAddressId(addressId);
    },
    [address],
  );

  const handlePaymentMethodChange = useCallback((method: PaymentMethod) => {
    setPaymentMethod(method);
    if (method !== PaymentMethod.ONLINE) {
      setSelectedCardId(null);
    }
  }, []);

  const handleBonusModeChange = useCallback(
    (mode: "spend" | "earn") => {
      setBonusMode(mode);
      if (mode === "earn") {
        bonuses.setBonusesToSpend(0);
      }
    },
    [bonuses],
  );

  const startOrder = useCallback(async () => {
    if (currentUser) {
      setStage(CartModalStage.CHECKOUT);
      return;
    }
    saveToStorage("afterAction", "cart");
    navigate(getAuthRoute());
    dispatch(closeCartModal());
  }, [currentUser, navigate, dispatch, getAuthRoute]);

  // Добавление/удаление подарка из корзины
  useEffect(() => {
    // Проверяем, есть ли уже подарок с таким ID в корзине
    const existingGift = cart.items.find(
      (item) => item.isGift && item.id === `gift_${selectedGiftItemId}`,
    );

    // Проверяем, есть ли вообще подарки в корзине
    const hasGift = cart.items.some((item) => item.isGift);

    // Если подарок выбран и его нет в корзине — добавляем
    if (selectedGiftItemId && !existingGift) {
      const giftItem = saleItemList.find(
        (item) => item.id === selectedGiftItemId,
      );
      if (giftItem) {
        const giftCartItem: CartItem = {
          id: String(selectedGiftItemId),
          name: `${giftItem.name} (подарок)`,
          price: 0,
          quantity: 1,
          minQuantity: 1,
          quantityStep: 1,
          image: giftItem.files?.[0] || "",
          isGift: true,
          categoryId: Number(giftItem.categoryId) || undefined,
        };
        dispatch(addGift(giftCartItem));
      }
      return;
    }

    // Если подарок не выбран, но есть подарки в корзине — удаляем
    if (!selectedGiftItemId && hasGift) {
      dispatch(removeGift());
    }
    // Убираем cart.items из зависимостей, чтобы избежать бесконечного цикла
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGiftItemId, saleItemList]);

  useEffect(() => {
    const payment = currentCluster?.payment;
    const isDelivery = deliveryType === DeliveryType.DELIVERY;
    const hasPaymentMethods =
      (isDelivery &&
        (payment?.cashOnDelivery ||
          payment?.cardOnDelivery ||
          payment?.onlineOnDelivery)) ||
      (!isDelivery &&
        (payment?.cashOnPickup ||
          payment?.cardOnPickup ||
          payment?.onlineOnPickup));

    let timeValidationError = null;
    if (desiredTime) {
      const selectedDate = new Date(desiredTime);
      if (!isTimeAvailable(selectedDate)) {
        timeValidationError =
          "Выбранное время недоступно. Пожалуйста, выберите другое время.";
      }
    }
    setValidationError(timeValidationError);

    let isGiftValid = true;
    if (promo.selectedPromo?.giftBySum) {
      isGiftValid =
        selectedGiftItemId !== undefined && selectedGiftItemId !== null;
    }

    const isCdek = deliveryService === DeliveryService.CDEK;
    const isPickupTariff =
      selectedTariff?.delivery_mode === 2 ||
      selectedTariff?.delivery_mode === 7;

    const disabled =
      !orderDetailsValid ||
      (!pickUpStore && deliveryType === DeliveryType.PICKUP) ||
      (hasPaymentMethods && paymentMethod === PaymentMethod.UNSET) ||
      deliveryType === DeliveryType.UNSET ||
      (deliveryType === DeliveryType.DELIVERY && !address.selectedAddressId) ||
      (deliveryType === DeliveryType.DELIVERY &&
        !isCdek &&
        !deliveryAvailable) ||
      (deliveryType === DeliveryType.DELIVERY && !isCdek && !deliveryStoreId) ||
      (deliveryType === DeliveryType.DELIVERY && isCdek && !selectedTariff) ||
      (deliveryType === DeliveryType.DELIVERY &&
        isCdek &&
        isPickupTariff &&
        !selectedPickupPoint) ||
      (promo.selectedPromo && !promo.validatePromo()) ||
      !!timeValidationError ||
      !isGiftValid;

    setOrderDisabled(Boolean(disabled));
  }, [
    orderDetailsValid,
    deliveryType,
    paymentMethod,
    pickUpStore,
    address.selectedAddressId,
    deliveryAvailable,
    deliveryStoreId,
    promo,
    currentCluster,
    desiredTime,
    isTimeAvailable,
    selectedGiftItemId,
    deliveryService,
    selectedTariff,
    selectedPickupPoint,
  ]);

  useEffect(() => {
    checkAfterAction();
  }, [checkAfterAction]);

  useEffect(() => {
    if (!currentCluster?.withDelivery) {
      setDeliveryType(DeliveryType.PICKUP);
    }
  }, [currentCluster]);

  const earnedBonuses =
    bonusMode === "spend"
      ? 0
      : calculateEarnedBonuses(
          calculations.subtotalWithDiscount,
          bonuses.bonusPercent,
          0,
        );

  const nextLevelText = getNextBonusLevelText(
    bonuses.totalSpent,
    bonuses.bonusRules,
  );

  useEffect(() => {
    if (forceStore && selectedStoreIds.length > 0 && !pickUpStore) {
      setPickUpStore(selectedStoreIds[0]);
    }
  }, [forceStore, selectedStoreIds, pickUpStore]);

  useEffect(() => {
    const payment = currentCluster?.payment;
    const isDelivery = deliveryType === DeliveryType.DELIVERY;

    if (isDelivery) {
      const methods: PaymentMethod[] = [
        payment?.cashOnDelivery ? PaymentMethod.CASH_ON_DELIVERY : null,
        payment?.cardOnDelivery ? PaymentMethod.CARD_ON_DELIVERY : null,
        payment?.onlineOnDelivery && !isManager ? PaymentMethod.ONLINE : null,
      ].filter((m): m is PaymentMethod => m !== null);

      if (methods.length === 1) {
        setPaymentMethod(methods[0]);
      }
    } else {
      const methods: PaymentMethod[] = [
        payment?.cashOnPickup ? PaymentMethod.CASH_ON_DELIVERY : null,
        payment?.cardOnPickup ? PaymentMethod.CARD_ON_DELIVERY : null,
        payment?.onlineOnPickup && !isManager ? PaymentMethod.ONLINE : null,
      ].filter((m): m is PaymentMethod => m !== null);

      if (methods.length === 1) {
        setPaymentMethod(methods[0]);
      }
    }
  }, [deliveryType, isManager, currentCluster]);

  useEffect(() => {
    if (!promo.selectedPromo?.giftBySum) {
      setSelectedGiftItemId(undefined);
    }
  }, [promo.selectedPromo]);

  useEffect(() => {
    setStoreCheckResult(null);
  }, [cart.items]);

  return {
    cart,
    stage,
    deliveryType,
    selectedAddressId: address.selectedAddressId,
    paymentMethod,
    orderNumber,
    addresses: address.addresses,
    selectedAddress: address.selectedAddress,
    orderDisabled,
    isOrderCreating,

    deliveryAvailable,
    deliveryStoreId,
    deliveryDistance,
    deliveryLoading,
    deliveryError,

    desiredTime,
    deliveryTimeDisplay,
    deliveryTimePickerVisible,
    openDeliveryTimePicker,
    closeDeliveryTimePicker,
    onDeliveryTimeSelect,
    deliveryTimeMinimumDate,

    itemsForOrder,
    itemsRemaining,
    storeCheckResult,

    itemsTotal: calculations.itemsTotal,
    deliveryFee: calculations.deliveryFee,
    discountAmount: calculations.discountAmount,
    subtotalWithDiscount: calculations.subtotalWithDiscount,
    totalWithDelivery: calculations.totalWithDelivery,

    currentBestRule: calculations.currentBestRule,
    nextBestRule: calculations.nextBestRule,
    bestPossibleRule: calculations.bestPossibleRule,

    selectedPromo: promo.selectedPromo,
    discountCode: promo.discountCode,
    applicablePromos: promo.applicablePromos,

    bonusBalance: bonuses.balance,
    bonusTotalSpent: bonuses.totalSpent,
    bonusPercent: bonuses.bonusPercent,
    bonusesToSpend: bonuses.bonusesToSpend,
    earnedBonuses,
    nextLevelText,
    maxWriteOff: calculateMaxWriteOff(
      calculations.itemsTotal,
      bonuses.balance,
      bonuses.writeOffPercent,
    ),

    storeList,
    pickUpStore,

    phone,
    comment,
    getValidationResult,
    inputHandler,
    setDirty,
    orderDetailsValid,
    MAX_COMMENT_LENGTH,

    getSaleItemName,
    getAddressList: address.loadAddresses,
    setStage,
    createOrder: handleCreateOrder,
    goBack,
    handleDeliveryTypeChange,
    handleAddressSelect,
    handlePaymentMethodChange,
    handleAddNewAddress: () => setStage(CartModalStage.ADD_ADDRESS),
    handleSelectPromo: promo.setSelectedPromo,
    setDiscountCode: promo.setDiscountCode,
    validatePromoBeforeOrder: promo.validatePromo,
    setBonusesToSpend: (value: number) => {
      if (bonusMode === "spend") {
        bonuses.setBonusesToSpend(value);
      }
    },
    loadBonuses: bonuses.loadBonuses,
    getBestDeliveryRuleText,
    roundMoney,
    calculateItemsTotal,
    isPromoApplicable,
    findApplicablePromos,
    validationError,
    handlePickUpStoreChange,
    startOrder,
    bonusMode,
    handleBonusModeChange,
    savedCards,
    selectedCardId,
    handleCardSelect,
    isYookassaEnabled,
    paymentToken,
    paymentId,
    setPaymentToken,
    setPaymentId,
    paymentUrl,
    setPaymentUrl,
    paymentFailed,
    paymentError,
    isTimeAvailable,
    selectedGiftItemId,
    setSelectedGiftItemId,
    cdekOptions,
    cdekLoading,
    cdekError,
    selectedTariff,
    selectedPickupPoint,
    setSelectedTariff,
    setSelectedPickupPoint,
    setDeliveryService,
    setCdekTariffCode,
    deliveryService,
    cutleryCount,
    incrementCutlery,
    decrementCutlery,
    applicableRules,
  };
};
